import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

const TRANSLATION_PREFIX = 'components.molecules.form';

/**
 * Marks the *optional* fields instead of starring the required ones.
 *
 * Most fields in these forms are required, so an asterisk on nearly every label carried no
 * information — it read as decoration. Marking the minority does.
 *
 * Returns a node, so it is passed as the `label` prop rather than through
 * `helper.getRequiredField`. Every caller still has the plain string available for the
 * accessible name; this is presentation only.
 */
export default function FieldLabel(props) {
  const { label, isRequired } = props;
  const { t } = useTranslation();

  if (!label) {
    return null;
  }

  if (isRequired) {
    return label;
  }

  return (
    <>
      {label}
      {' '}
      <Box
        color="text.disabled"
        component="span"
        sx={{ fontWeight: 400 }}
      >
        {`· ${t(`${TRANSLATION_PREFIX}.optional`)}`}
      </Box>
    </>
  );
}

FieldLabel.propTypes = {
  label: PropTypes.string,
  isRequired: PropTypes.bool,
};

/**
 * The same label for the controls that have no MUI input to notch it into — segmented
 * buttons, option cards, the upload dropzone. It borrows `MuiInputLabel`'s weight and the
 * theme's muted `text.secondary` so it reads identically to the fields around it.
 */
export function StandaloneFieldLabel(props) {
  const {
    label, isRequired, id, sx,
  } = props;

  if (!label) {
    return null;
  }

  return (
    <Typography
      color="text.secondary"
      component="div"
      id={id}
      // `sx` last so a caller can drop the stacking margin — `Segmented` positions its
      // label into the control's top border instead, to line up with the inputs beside it.
      sx={{ fontWeight: 600, mb: 1, ...sx }}
      variant="caption"
    >
      <FieldLabel isRequired={isRequired} label={label} />
    </Typography>
  );
}

StandaloneFieldLabel.propTypes = {
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  id: PropTypes.string,
  sx: PropTypes.object,
};
